import { useEffect, useRef, useState } from 'react';

/**
 * Drop-in replacement for useState that persists the value to sessionStorage,
 * keyed by `key`. Used to retain list-page filters / search / pagination when the
 * user opens a record and navigates back (the list unmounts and remounts).
 *
 * sessionStorage (not localStorage) is intentional: state survives in-tab
 * navigation and refresh, but is cleared when the tab closes — so stale filters
 * don't linger across sessions.
 *
 * @param {string} key Unique storage key (namespace per page+field, e.g. 'vehicleList:filters').
 * @param {*} defaultValue Initial value when nothing is stored yet.
 * @returns {[*, Function]} Same tuple shape as useState; setter supports functional updates.
 */
export default function usePersistedState(key, defaultValue) {
	const keyRef = useRef(key);

	const [value, setValue] = useState(() => {
		try {
			const stored = window.sessionStorage.getItem(key);
			return stored !== null ? JSON.parse(stored) : defaultValue;
		} catch (error) {
			return defaultValue;
		}
	});

	useEffect(() => {
		try {
			window.sessionStorage.setItem(keyRef.current, JSON.stringify(value));
		} catch (error) {
			// Ignore quota / serialization errors — persistence is best-effort.
		}
	}, [value]);

	return [value, setValue];
}

/** Clear one or more persisted keys (e.g. on logout or an explicit "reset all"). */
export const clearPersistedState = (...keys) => {
	keys.forEach((key) => {
		try {
			window.sessionStorage.removeItem(key);
		} catch (error) {
			// no-op
		}
	});
};
